/**
 * The channels a message can travel on in this deployment.
 *
 * Per notification row rather than per tool: WhatsApp templates are approved
 * one kind at a time, so a deployment can remind about a session on WhatsApp
 * and still announce the booking by email only. The two older flags stay, as
 * the server derives them from the rows.
 *
 * Nothing here is per patient. Whether a given patient hears from us on
 * WhatsApp is their own consent, given in their portal.
 */
export type WhatsAppNoticeRow =
    | "booking"
    | "change"
    | "reminder"
    | "payment_request"
    | "payment_reminder";

export type WhatsAppNotices = Record<WhatsAppNoticeRow, boolean>;

export interface ReminderChannels {
    whatsapp_session_messages: boolean;
    whatsapp_payment_messages: boolean;
    whatsapp_notices: WhatsAppNotices;
}

/** Every row, in the order the settings list them. */
export const WHATSAPP_NOTICE_ROWS: readonly WhatsAppNoticeRow[] = [
    "booking",
    "change",
    "reminder",
    "payment_request",
    "payment_reminder",
];

/** Every flag false: what the settings assume until the server says otherwise. */
export const EMAIL_ONLY: ReminderChannels = {
    whatsapp_session_messages: false,
    whatsapp_payment_messages: false,
    whatsapp_notices: {
        booking: false,
        change: false,
        reminder: false,
        payment_request: false,
        payment_reminder: false,
    },
};

export const sameReminderChannels = (a: ReminderChannels, b: ReminderChannels): boolean =>
    a.whatsapp_session_messages === b.whatsapp_session_messages &&
    a.whatsapp_payment_messages === b.whatsapp_payment_messages &&
    WHATSAPP_NOTICE_ROWS.every((row) => a.whatsapp_notices[row] === b.whatsapp_notices[row]);

export const sessionNoticesOnWhatsApp = (channels: ReminderChannels): boolean =>
    channels.whatsapp_notices.booking ||
    channels.whatsapp_notices.change ||
    channels.whatsapp_notices.reminder;

export const paymentNoticesOnWhatsApp = (channels: ReminderChannels): boolean =>
    channels.whatsapp_notices.payment_request || channels.whatsapp_notices.payment_reminder;
